import React from "react";
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";

const BAR_COLOR = "#69A3BB";
const LINE_COLORS = ["#FFA737", "#01755C", "#69A3BB"];

const prepareChartData = (rawArray, selected) => {
    return rawArray.map((item) => {
        const row = { cw: item.calendar_week };
        selected.forEach((param) => {
            const [section, name] = param.key.split(".");
            const match = item[section]?.find((entry) => entry.name === name);
            if (match) {
                row[param.key] = match.value;
                param.unit = match.unit;
            } else {
                row[param.key] = null;
            }
        });
        return row;
    });
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <div style={{
            backgroundColor: 'white',
            border: '1px solid #FFA737',
            padding: '10px',
            borderRadius: '5px',
            fontSize: '14px',
            fontWeight: '600'
        }}>
            <p ><strong>Calendar Week: {label}</strong></p>
            {payload.map((entry, index) => (
                <p key={index} style={{ color: entry.color, margin: 0 }}>
                    {entry.name}: {entry.value}
                </p>
            ))}
        </div>
    );
};


const DataVisualizationChart = ({ data = [], selected = [] }) => {
    if (!data?.length || !selected?.length) {
        return <div className="text-gray-500">No data to display.</div>;
    }

    const transformedData = prepareChartData(data, selected);

    const barParam = selected.find(p => p.key === "harvest.harvested_weight");
    const lineParams = selected.filter(p => p.key !== "harvest.harvested_weight");

    const allParams = [...lineParams, barParam].filter(Boolean);

    return (
        <div style={{ width: "100%", height: "400px" }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={transformedData}>
                    <CartesianGrid stroke="#f0f0f0" />
                    <XAxis
                        dataKey="cw"
                        axisLine={true}
                        tickLine={true}
                        tick={{ fontSize: 12, fill: "#333" }}
                        label={{
                            value: "Calendar Week",
                            position: "insideBottom",
                            offset: -5,
                            style: { fill: "#333", fontSize: 14 }
                        }}
                    />

                    {allParams.map((param, i) => {
                        const isBar = param.key === "harvest.harvested_weight";
                        const lineColorIndex = lineParams.findIndex(p => p.key === param.key);
                        const color = isBar ? BAR_COLOR : LINE_COLORS[lineColorIndex % LINE_COLORS.length];

                        return (
                            <YAxis
                                key={param.key}
                                yAxisId={param.key}
                                orientation={i % 2 === 0 ? "right" : "left"}
                                stroke={color}
                                label={{
                                    value: `${param.name} (${param.unit || ""})`,
                                    angle: -90,
                                    position: i % 2 === 0 ? "insideLeft" : "insideRight",
                                    offset: 60,
                                    fill: color,
                                    style: {
                                        textAnchor: "middle",
                                        dominantBaseline: "middle",
                                    },

                                }}
                                width={80}
                            />
                        );
                    })}

                    <Tooltip content={<CustomTooltip />} />
                    <Legend layout="horizontal" verticalAlign="top" align="left" wrapperStyle={{ paddingBottom: "20px" }} />
                    {barParam && (
                        <Bar
                            key={barParam.key}
                            yAxisId={barParam.key}
                            dataKey={barParam.key}
                            fill={BAR_COLOR}
                            name={`${barParam.name} (${barParam.unit || ""})`}
                            barSize={25}
                        />
                    )}

                    {lineParams.map((param, i) => (
                        <Line
                            key={param.key}
                            yAxisId={param.key}
                            type="monotone"
                            dataKey={param.key}
                            name={`${param.name} (${param.unit || ""})`}
                            stroke={LINE_COLORS[i % LINE_COLORS.length]}
                            strokeWidth={2}
                            dot={false}
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default DataVisualizationChart;
